import { FormLabel } from '@mui/material'
import * as React from 'react'
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { MaskedInput } from './MaskedInput'

interface DateInputControllerProps<T extends FieldValues> {
  max?: string // Date string YYYY-MM-DD
  min?: string // Date string YYYY-MM-DD
  control: Control<T>
  name: Path<T>
  label?: string
  required?: boolean
  rules?: RegisterOptions<T>
}

interface DateInputProps {
  value: string | undefined
  onChange: (value: string) => void
  error: { message?: string } | undefined
  required?: boolean
  label?: string
  name?: string
}

const preprocessInput = (input: string): string => {
  const cleanInput = input.replace(/\D/g, '')
  if (cleanInput.length === 0) return ''

  return cleanInput
}

const formatDateForDisplay = (value: string | undefined): string => {
  if (!value) return ''
  const [year, month, day] = value.split('-')
  return `${month}${day}${year}`
}

// input: '' or mm or mmdd or mmddyyyy
// output: -- or -m- or -mm- or -mm-d or -mm-dd or yyyy-mm-dd
const parseDateInput = (input: string): string => {
  // Handle empty input
  if (!input) return '--'

  // Handle raw number format (mmddyyyy)
  const cleanInput = preprocessInput(input)
  if (cleanInput.length > 8) return '--'
  if (cleanInput.length <= 2) {
    return `-${cleanInput}-`
  }
  if (cleanInput.length <= 4) {
    return `-${cleanInput.slice(0, 2)}-${cleanInput.slice(2)}`
  }
  if (cleanInput.length <= 8) {
    return `${cleanInput.slice(4)}-${cleanInput.slice(0, 2)}-${cleanInput.slice(2, 4)}`
  }
  return ''
}

export const DateInput = ({ value, onChange, error, required, label, name }: DateInputProps) => {
  return (
    <>
      <FormLabel>{label}</FormLabel>
      <MaskedInput
        name={name}
        format="##/##/####"
        mask="_"
        value={formatDateForDisplay(value)}
        onChange={(e) => onChange(parseDateInput(e.target.value))}
        error={Boolean(error)}
        required={required}
        fullWidth
        placeholder="mm/dd/yyyy"
        helperText={error?.message}
      />
    </>
  )
}

export const DateInputController = <T extends FieldValues>({
  control,
  name,
  label,
  required,
  min,
  max,
  rules,
}: DateInputControllerProps<T>) => {
  const intl = useIntl()

  const minMaxValid = (v: string) => {
    if (!min && !max) return true
    if (!v) return intl.formatMessage({ id: 'common.validation.required' })
    const date = new Date(v)
    if (isNaN(date.getTime())) {
      return intl.formatMessage({ id: 'common.validation.date.invalid' })
    }
    if (min && date < new Date(min)) {
      return intl.formatMessage({ id: 'common.validation.date.min' }, { min })
    }
    if (max && date > new Date(max)) {
      return intl.formatMessage({ id: 'common.validation.date.max' }, { max })
    }
    return true
  }
  return (
    <Controller
      control={control}
      name={name}
      rules={{
        validate: minMaxValid,
        ...rules,
      }}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <DateInput value={value} onChange={onChange} error={error} required={required} label={label} />
      )}
    />
  )
}

const dateToIsoDate = (date: Date) => {
  const offset = date.getTimezoneOffset()
  const offsetCorrectedDate = new Date(date.getTime() - offset * 60 * 1000)
  return offsetCorrectedDate.toISOString().slice(0, 10) // 'YYYY-MM-DD'
}

export const birthdateMinMax = {
  min: dateToIsoDate(new Date(1901, 0, 1)),
  max: dateToIsoDate(new Date(new Date().getFullYear() - 14, 11, 31)),
}
